import { matchingFlagRestaurant } from '../../../../../customerRequests/quantityFlagRestaurant';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getRestaurantsForProductOverride,
  applyQuantityOverride, previewQuantityOverride,
  type FlaggedProductWithRestaurant,
  type RestaurantForProductOverride,
} from '../../../../../../services/admin/adminService';
import { Scale } from 'lucide-react';
import type { TFn } from '../../types';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../../../../ui/InventorySlideOver';
import { QuantityChangeReview } from '../../../../../orderhistory/QuantityChangeReview';

// Icon button + slide-over to review and apply a quantity-info override for a flagged product. Lists every restaurant
// that ordered the product (matched the same way overrides are keyed) and lets the operator pick which
// ones to apply to. Each apply backfills existing orders AND auto-applies to future invoices.
export function ApplyOverrideButton({ flag, t }: { flag: FlaggedProductWithRestaurant; t: TFn }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantForProductOverride[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Override form — seeded from the current quantityInfo (piece override is the common case here).
  const [review, setReview] = useState<Awaited<ReturnType<typeof previewQuantityOverride>> | null>(null);
  const applicablePreviews = review?.previews.filter(item => !item.preview.notConverted) ?? [];
  const [stdUnit, setStdUnit] = useState<string>(flag.quantityInfo?.stdUnit ?? 'p');
  const [stdAmount, setStdAmount] = useState<string>(String(flag.quantityInfo?.stdAmount ?? 1));
  const [stdMultiplier, setStdMultiplier] = useState<string>(String(flag.quantityInfo?.stdMultiplier ?? 1));

  const inputCls = cn('px-2 py-1 rounded-md border w-20', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);

  const load = async () => {
    setOpen(true);
    if (restaurants !== null) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getRestaurantsForProductOverride(flag.platform, flag.articleId, flag.productName);
      setRestaurants(list);
      // AI Checker overloads restaurantId with an audit trace id. Only a returned
      // restaurant match or one unambiguous actual restaurant may be preselected.
      const restaurantId = matchingFlagRestaurant(list, flag.setupSource?.restaurantId ?? flag.restaurantId);
      setSelected(new Set(list.filter(r => r.restaurantId === restaurantId).map(r => r.restaurantPlatformId)));
    } catch (e: any) {
      setError(e.message || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const overrideArgs = () => ({
    platform: flag.platform,
    articleId: flag.articleId,
    productName: flag.productName,
    stdAmount: parseFloat(stdAmount),
    stdMultiplier: parseFloat(stdMultiplier),
    stdUnit,
    weightString: flag.quantityInfo?.weightString ?? flag.productName,
    restaurantPlatformIds: Array.from(selected),
  });

  const nameOf = (restaurantPlatformId: string) => restaurants?.find(r => r.restaurantPlatformId === restaurantPlatformId)?.restaurantName ?? restaurantPlatformId;

  const openReview = async () => {
    if (busy || selected.size === 0) return;
    const amount = parseFloat(stdAmount);
    const multiplier = parseFloat(stdMultiplier);
    if (isNaN(amount) || amount <= 0 || isNaN(multiplier) || multiplier <= 0) {
      setError(t('adminTesting.override.invalidValues'));
      return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setReview(await previewQuantityOverride(overrideArgs()));
    } catch (e: any) {
      setError(e.message || t('orderHistory.quantityChange.previewFailed'));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (busy || !review || applicablePreviews.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await applyQuantityOverride({
        ...overrideArgs(),
        restaurantPlatformIds: applicablePreviews.map(item => item.restaurantPlatformId),
        previewFingerprints: Object.fromEntries(applicablePreviews.map(item => [item.restaurantPlatformId, item.preview.fingerprint])),
      });
      setDone(t('adminTesting.override.applied', { restaurants: res.restaurantsApplied, orders: res.totalUpdated }));
      if (res.failed.length) {
        const failed = t('orderHistory.quantityChange.failedRestaurants', { names: res.failed.map(item => nameOf(item.restaurantPlatformId)).join(', ') });
        setError(res.failed.some(item => item.error === 'unsafe_quantity_identity')
          ? `${failed}. ${t('orderHistory.quantityChange.notConverted')}` : failed);
      }
      setReview(null);
      // Refresh the affected list so the "has override" badges update.
      const list = await getRestaurantsForProductOverride(flag.platform, flag.articleId, flag.productName);
      setRestaurants(list);
    } catch (e: any) {
      setError(e.message || 'Failed to apply override');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={load}
        title={t('adminTesting.override.buttonTitle')}
        className={cn('p-1 rounded-md text-gray-500 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors')}
      >
        <Scale className="w-4 h-4" />
      </button>
      {open && createPortal(
        <InventorySlideOver isOpen onClose={() => setOpen(false)} closeLabel={t('common.close')}
          title={t(review ? 'orderHistory.quantityChange.reviewTitle' : 'adminTesting.override.title')} subtitle={`${flag.productName} · ${flag.platform}`}
          footer={review ? <>
            <button type="button" disabled={busy} className={slideOverCancelClass} onClick={() => { setReview(null); setError(null); }}>{t('common.back')}</button>
            <button type="button" disabled={busy || applicablePreviews.length === 0} className={slideOverPrimaryClass} onClick={apply}>
              {busy ? t('adminTesting.override.applying') : t('adminTesting.override.applyTo', { count: applicablePreviews.length })}
            </button>
          </> : <>
            <button type="button" className={slideOverCancelClass} onClick={() => setOpen(false)}>{t('common.close')}</button>
            <button type="button" disabled={busy || selected.size === 0} className={slideOverPrimaryClass} onClick={openReview}>
              {busy ? t('orderHistory.quantityChange.loading') : t('orderHistory.quantityChange.reviewButton')}
            </button>
          </>}>
          {review ? <div className="space-y-6">
            {review.previews.map(({ restaurantPlatformId, preview }) => <div key={restaurantPlatformId} className="space-y-2">
              <h3 className={cn(primaryText, semiBoldText)}>{nameOf(restaurantPlatformId)}</h3>
              <QuantityChangeReview preview={preview} />
            </div>)}
            {review.failed.length > 0 && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{t('orderHistory.quantityChange.previewFailedRestaurants', { names: review.failed.map(nameOf).join(', ') })}</div>}
            {error && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{error}</div>}
          </div> : <div className="space-y-4">
            {/* Override values */}
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.override.unit')}</span>
                <select value={stdUnit} onChange={(e) => setStdUnit(e.target.value)} className={cn(inputCls, 'w-auto')}>
                  <option value="p">{t('adminTesting.override.unitPiece')}</option>
                  <option value="g">g</option>
                  <option value="ml">ml</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.override.amount')}</span>
                <input type="number" min="0" step="any" value={stdAmount} onChange={(e) => setStdAmount(e.target.value)} className={inputCls} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.override.multiplier')}</span>
                <input type="number" min="0" step="any" value={stdMultiplier} onChange={(e) => setStdMultiplier(e.target.value)} className={inputCls} />
              </label>
              <span className={cn(secondaryText, 'text-xs pb-1.5')}>
                = {stdMultiplier} × {stdAmount}{stdUnit}
              </span>
            </div>

            {/* Affected restaurants */}
            <div>
              <div className={cn(secondaryText, 'text-xs mb-1')}>{t('adminTesting.override.restaurantsHeading')}</div>
              {loading ? (
                <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.override.loading')}</div>
              ) : !restaurants || restaurants.length === 0 ? (
                <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.override.noRestaurants')}</div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-3 pb-1">
                    <button onClick={() => setSelected(new Set(restaurants.map(r => r.restaurantPlatformId)))} className={cn('text-xs text-blue-600 dark:text-blue-400 hover:underline')}>{t('adminTesting.override.selectAll')}</button>
                    <button onClick={() => setSelected(new Set())} className={cn('text-xs text-blue-600 dark:text-blue-400 hover:underline')}>{t('adminTesting.override.selectNone')}</button>
                  </div>
                  {restaurants.map(r => (
                    <label key={r.restaurantPlatformId} className={cn('flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer', 'hover:bg-gray-100 dark:hover:bg-gray-700/50')}>
                      <input type="checkbox" checked={selected.has(r.restaurantPlatformId)} onChange={() => toggle(r.restaurantPlatformId)} />
                      <span className={cn(primaryText, smallTextSize, 'flex-1 truncate')} title={r.restaurantName}>{r.restaurantName}</span>
                      {r.hasOverride && <span className={cn('text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300')}>{t('adminTesting.override.hasOverride')}</span>}
                      <span className={cn(secondaryText, 'text-xs whitespace-nowrap')}>{t('adminTesting.override.occurrences', { count: r.occurrences })}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
            {done && <div className={cn('text-xs text-green-600 dark:text-green-400')}>{done}</div>}

          </div>}
        </InventorySlideOver>,
        document.body
      )}
    </>
  );
}
